import { client } from './client'
import { getCsrf, getCookie } from './authentication'
import { TemplateAttributeInput } from './types'
import { CustomError } from '../CustomError'
import { CSRF_TOKEN_NAME } from '@/constants'

/**
 * Create a new template attribute
 * @param documentPageId
 * @param payload
 * @returns
 */
export const createTemplateAttribute = async (
  documentPageId: string,
  payload: TemplateAttributeInput,
) => {
  await getCsrf()
  const resp = await client.POST(
    '/api/v1/projects/{document_page_id}/template_attributes',
    {
      headers: {
        'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
      },
      params: {
        path: {
          document_page_id: documentPageId,
        },
      },
      body: payload,
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to create template attribute'
    throw new CustomError(resp, message)
  }

  return resp.data
}

/**
 * Bulk create template attributes
 * @param documentPageId
 * @param payloads
 * @returns
 */
export const bulkCreateTemplateAttributes = async (
  documentPageId: string,
  payloads: TemplateAttributeInput[],
) => {
  await getCsrf()
  const resp = await client.POST(
    '/api/v1/projects/{document_page_id}/template_attributes/bulk',
    {
      headers: {
        'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
      },
      params: {
        path: {
          document_page_id: documentPageId,
        },
      },
      body: payloads,
    },
  )
  if (resp.error || !resp.response.ok) {
    const message = 'Failed to bulk create template attributes'
    throw new CustomError(resp, message)
  }

  return resp.data
}

/**
 * Bulk delete template attributes
 * @param documentPageId
 * @param payloads
 * @returns
 */
export const bulkDeleteTemplateAttributes = async (
  documentPageId: string,
  templateIds: string[],
) => {
  await getCsrf()
  const resp = await client.DELETE(
    '/api/v1/projects/{document_page_id}/template_attributes/bulk',
    {
      headers: {
        'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
      },
      params: {
        path: {
          document_page_id: documentPageId,
        },
      },
      body: templateIds,
    },
  )
  if (resp.error || !resp.response.ok) {
    const message = 'Failed to bulk delete template attributes'
    throw new CustomError(resp, message)
  }

  return resp.data
}

/**
 * Bulk update template attributes
 * @param documentPageId
 * @param payloads
 * @returns
 */
export const bulkUpdateTemplateAttributes = async (
  documentPageId: string,
  payloads: Array<{
    id: string
    template_values: Record<string, any>
  }>,
) => {
  await getCsrf()
  const resp = await client.POST(
    '/api/v1/projects/{document_page_id}/template_attributes/bulk_update',
    {
      headers: {
        'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
      },
      params: {
        path: {
          document_page_id: documentPageId,
        },
      },
      body: payloads as any,
    },
  )
  if (resp.error || !resp.response.ok) {
    const message = 'Failed to bulk update template attributes'
    throw new CustomError(resp, message)
  }

  return resp.data
}

/**
 * Create a new template attribute
 * @param templateAttributeId
 * @param payload
 * @returns
 */
export const updateTemplateAttribute = async (
  templateAttributeId: string,
  payload: TemplateAttributeInput,
) => {
  await getCsrf()
  const resp = await client.PUT(
    '/api/v1/projects/template_attributes/{template_attribute_id}',
    {
      headers: {
        'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
      },
      params: {
        path: {
          template_attribute_id: templateAttributeId,
        },
      },
      body: payload,
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to update template attribute'
    throw new CustomError(resp, message)
  }

  return resp.data
}

/**
 * Delete a template attribute
 * @param templateAttributeId
 * @returns
 */
export const deleteTemplateAttribute = async (templateAttributeId: string) => {
  await getCsrf()
  const resp = await client.DELETE(
    '/api/v1/projects/template_attributes/{template_attribute_id}',
    {
      headers: {
        'X-CSRFToken': getCookie(CSRF_TOKEN_NAME),
      },
      params: {
        path: {
          template_attribute_id: templateAttributeId,
        },
      },
      parseAs: 'text',
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to delete template attribute'
    throw new CustomError(resp, message)
  }

  return true
}

/**
 * Get a template attribute
 * @param templateAttributeId
 * @returns
 */
export const getTemplateAttribute = async (templateAttributeId: string) => {
  const resp = await client.GET(
    '/api/v1/projects/template_attributes/{template_attribute_id}',
    {
      params: {
        path: {
          template_attribute_id: templateAttributeId,
        },
      },
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to get template attribute'
    throw new CustomError(resp, message)
  }

  return resp.data
}

/**
 * List all template attributes
 * @param documentPageId
 * @returns
 */
export const listTemplateAttributes = async (documentPageId: string) => {
  const resp = await client.GET(
    '/api/v1/projects/{document_page_id}/template_attributes',
    {
      params: {
        path: {
          document_page_id: documentPageId,
        },
      },
    },
  )

  if (resp.error || !resp.response.ok) {
    const message = 'Failed to list template attributes'
    throw new CustomError(resp, message)
  }

  return resp.data
}
